import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';
import { useAudio, AudioProvider } from './DassPage';
import { saveOfflineAssessment } from '../utils/offlineAssessment';
import { toast } from 'react-toastify';
import './StarOfWellbeingAssessment.scss';
import './StarOfWellbeingCompletion.scss';

interface AssessmentResponse {
  questionId: string;
  answer: number;
  timestamp: Date;
}

interface AnswerOption {
  value: number;
  label: string;
  stars: number;
  description: string;
}

// Helper function to get appropriate background image
const getBackgroundImage = () => {
  return window.innerWidth >= 1024
    ? 'url(/BackgroundSamples/welness-dass-walpaper-desktop.png)'
    : 'url(/BackgroundSamples/welness-dass-wallpaper.png)';
};

const StarOfWellbeingAssessment: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, isAuthenticated } = useAuth();
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [responses, setResponses] = useState<AssessmentResponse[]>([]);

  // Determine if this is the public version
  const isPublicVersion = location.pathname.startsWith('/star-of-well-being');

  // Use shared audio context
  const { isPlaying, volume, isMuted, togglePlayPause, toggleMute, setVolume } = useAudio();

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVolume(Number(e.target.value));
  };

  const totalQuestions = 10; // Star of Well-Being has 10 questions

  // Star of Well-Being Assessment Questions
  const questions = [
    {
      id: '1',
      question: 'How connected do you feel to your values, purpose, or spiritual beliefs?',
      category: 'inherent',
      attribute: 'spiritual',
      tips: 'Consider whether you feel guided by a deeper sense of meaning, inner peace, or moral compass in daily life.'
    },
    {
      id: '2',
      question: 'How well do your eating habits support your health and energy needs?',
      category: 'inherent',
      attribute: 'nutritional',
      tips: 'Think about food quality, balance, hydration, and whether you eat in a way that sustains your well-being.'
    },
    {
      id: '3',
      question: 'How would you describe your current physical health?',
      category: 'inherent',
      attribute: 'physical',
      tips: 'Include energy levels, fitness, mobility, and absence or presence of illness or pain.'
    },
    {
      id: '4',
      question: 'How well do you manage emotions, thoughts, and decision-making?',
      category: 'inherent',
      attribute: 'emotio-psycho-rational',
      tips: 'Consider emotional stability, stress coping, self-talk, and clarity in judgment.'
    },
    {
      id: '5',
      question: 'How clear and fulfilled do you feel in your life\'s purpose and legacy?',
      category: 'inherent',
      attribute: 'legendarial',
      tips: 'Reflect on whether your actions align with your long-term goals and how you contribute beyond yourself.'
    },
    {
      id: '6',
      question: 'How well do your traditions, beliefs, and cultural identity support your well-being?',
      category: 'coherent',
      attribute: 'religio-traditio-cultural',
      tips: 'Consider harmony between your personal values and the traditions or culture you engage with.'
    },
    {
      id: '7',
      question: 'How secure and confident do you feel about your financial situation?',
      category: 'coherent',
      attribute: 'financial',
      tips: 'Include income stability, ability to meet needs, savings, and financial planning.'
    },
    {
      id: '8',
      question: 'How supportive is your physical environment for your health and well-being?',
      category: 'coherent',
      attribute: 'environmental',
      tips: 'Think about home, workplace, access to nature, and safety in your surroundings.'
    },
    {
      id: '9',
      question: 'How strong and supportive are your personal and professional relationships?',
      category: 'coherent',
      attribute: 'socio-relational',
      tips: 'Consider trust, communication, and sense of belonging in your relationships.'
    },
    {
      id: '10',
      question: 'How satisfied and engaged are you in your current work or daily role?',
      category: 'coherent',
      attribute: 'occupational',
      tips: 'Reflect on fulfillment, work-life balance, and whether your role aligns with your skills and passions.'
    }
  ];

  // Star of Well-Being scale options with star ratings
  const answerOptions: AnswerOption[] = [
    {
      value: 1,
      label: 'Needs Support',
      stars: 1,
      description: 'Significant challenges in this area'
    },
    {
      value: 2,
      label: 'Taking Steps',
      stars: 2,
      description: 'Actively working on improvement'
    },
    {
      value: 3,
      label: 'Feeling Empowered',
      stars: 3,
      description: 'Consistent maintenance of well-being'
    },
    {
      value: 4,
      label: 'At Your Well-Being State',
      stars: 4,
      description: 'Excellent state of well-being'
    }
  ];

  const renderStars = (count: number) => {
    return (
      <>
        {Array.from({ length: count }, (_, i) => (
          <span key={i} className="material-symbols-outlined">star</span>
        ))}
      </>
    );
  };

  const currentQuestionData = questions[currentQuestion - 1];
  const currentResponse = currentQuestionData ? responses.find(r => r.questionId === currentQuestionData.id) : null;
  const progress = (currentQuestion / totalQuestions) * 100;

  const saveCurrentResponse = (answer: number) => {
    if (!currentQuestionData) return;

    const newResponse: AssessmentResponse = {
      questionId: currentQuestionData.id,
      answer,
      timestamp: new Date()
    };

    setResponses(prev => {
      const filtered = prev.filter(r => r.questionId !== currentQuestionData.id);
      return [...filtered, newResponse];
    });
  };

  const handleNext = () => {
    if (currentQuestion < totalQuestions) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentQuestion > 1) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    if (responses.length < totalQuestions) {
      alert('Please answer all questions before submitting.');
      return;
    }

    setIsLoading(true);

    try {
      // Test 1: Validate data structure
      const formattedResponses = responses.map(r => {
        const question = questions.find(q => q.id === r.questionId);
        if (!question) {
          throw new Error(`Question with id ${r.questionId} not found`);
        }
        return {
          attribute: question.attribute,
          score: r.answer,
          timestamp: r.timestamp
        };
      });

      // Test 2: Check all required attributes are present
      const requiredAttributes = ['spiritual', 'nutritional', 'physical', 'emotio-psycho-rational', 'legendarial', 'religio-traditio-cultural', 'financial', 'environmental', 'socio-relational', 'occupational'];
      const presentAttributes = formattedResponses.map(r => r.attribute);
      const missingAttributes = requiredAttributes.filter(attr => !presentAttributes.includes(attr));

      if (missingAttributes.length > 0) {
        throw new Error(`Missing attributes: ${missingAttributes.join(', ')}`);
      }

      // Test 3: Validate score ranges
      for (const response of formattedResponses) {
        if (typeof response.score !== 'number' || response.score < 1 || response.score > 4) {
          throw new Error(`Invalid score ${response.score} for attribute ${response.attribute}. Must be between 1 and 4.`);
        }
      }

      // Prepare assessment data with "dass-star" type
      const assessmentData = {
        type: 'dass-star',
        responses: formattedResponses,
        metadata: {
          totalQuestions,
          completedAt: new Date().toISOString(),
          assessmentType: 'DASS Star Assessment'
        }
      };

      if (isPublicVersion && !isAuthenticated) {
        // Public version - save locally for later sync

        const offlineData = {
          assessmentData,
          responses,
          duration: 0,
          savedAt: new Date().toISOString(),
          type: 'dass-star'
        };

        saveOfflineAssessment(offlineData, 'dass-star');

        // Show user feedback
        toast.info('Assessment saved locally! Your results will be synced to your account when you log in.', {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        // Navigate to completion page with local data
        navigate('/star-of-well-being/assessment/completion', {
          state: { assessmentResult: null, responses: responses, savedLocally: true, offlineData }
        });
      } else if (isAuthenticated && token) {
        // Authenticated version - save to database
        console.log('📝 Authenticated assessment - saving to database...');

        const response = await fetch(API_CONFIG.ENDPOINTS.STAR_OF_WELLBEING, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          credentials: 'include',
          body: JSON.stringify(assessmentData)
        });

        if (response.ok) {
          const result = await response.json();

          // Navigate to completion page with assessment result
          navigate('/panel/star-of-wellbeing/assessment/completion', {
            state: { assessmentResult: result.data, responses: responses, savedToDatabase: true }
          });
        } else {
          const errorData = await response.json();

          throw new Error(`Failed to save assessment: ${errorData.message}`);
        }
      } else {
        // Fallback error for unexpected state
        // Don't show error for panel routes (should be protected by authentication)
        if (!isPublicVersion) {

          return;
        }
        throw new Error('Unable to save assessment. Please try again.');
      }
    } catch (error) {

      alert(`Error saving assessment: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="star-assessment-page" style={{ backgroundImage: getBackgroundImage() }}>
        <div className="star-assessment-page__loading">
          <div className="star-assessment-page__spinner"></div>
          <p>Submitting your assessment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="star-assessment-page" style={{ backgroundImage: getBackgroundImage() }}>
      {/* Header */}
      <div className="star-assessment-page__header">
        <div className="star-assessment-page__progress">
          <div className="star-assessment-page__progress-bar">
            <div
              className="star-assessment-page__progress-fill"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
        <button
          className="star-assessment-page__back-btn"
          onClick={() => navigate('/panel/learn/')}
        >
          Home
        </button>
      </div>

      {/* Content */}
      <div className="star-assessment-page__content">
        {/* Question Section */}
        <div className="star-assessment-page__question-section">
          <p className="star-assessment-page__question">
            <strong>Question:</strong> {currentQuestionData?.question}
          </p>
          <p className="star-assessment-page__tips">
            <strong>Tips:</strong> {currentQuestionData?.tips}
          </p>
        </div>

        {/* Options Grid */}
        <div className="star-assessment-page__options-grid">
          {answerOptions.map((option) => (
            <div
              key={option.value}
              className={`star-assessment-page__option-card ${
                currentResponse?.answer === option.value ? 'selected' : ''
              }`}
              onClick={() => saveCurrentResponse(option.value)}
            >
              <div className="star-assessment-page__option-stars">
                {renderStars(option.stars)}
              </div>
              <h3 className="star-assessment-page__option-title">{option.label}</h3>
              <p className="star-assessment-page__option-description">{option.description}</p>
            </div>
          ))}
        </div>

        {/* Navigation Buttons */}
        <div className="star-assessment-page__buttons">
          <button
            className="star-assessment-page__btn star-assessment-page__btn--back"
            onClick={handleBack}
            disabled={currentQuestion === 0}
          >
            <span className="material-symbols-outlined">chevron_left</span> Back
          </button>
          <button
            className="star-assessment-page__btn star-assessment-page__btn--continue"
            onClick={handleNext}
            disabled={!currentResponse}
          >
            Continue <span className="material-symbols-outlined">chevron_right</span>
          </button>
        </div>
      </div>

      {/* Audio Controls */}
      <div className="star-assessment-page__audio-controls">
        <button className="star-assessment-page__audio-btn" onClick={togglePlayPause}><span className="material-symbols-outlined">{isPlaying ? 'pause' : 'play_arrow'}</span></button>
        <button className="star-assessment-page__audio-btn" onClick={toggleMute}><span className="material-symbols-outlined">{isMuted ? 'volume_off' : 'volume_up'}</span></button>
        <span className="star-assessment-page__volume-text">Volume</span>
        <div className="star-assessment-page__volume-bar">
          <input
            type="range"
            min="0"
            max="100"
            value={volume}
            onChange={handleVolumeChange}
            className="star-assessment-page__volume-fill"
            style={{ '--volume': `${volume}%` } as React.CSSProperties}
          />
        </div>
      </div>
    </div>
  );
};

export default StarOfWellbeingAssessment;


